import React, { useState } from "react";
import { X, Plus, Link2, Pencil, Upload } from "lucide-react";
import type {
  OptionDto,
  OptionAttributeDto,
  OptionAttributeValueDto,
  MaterialDto,
  CreateOptionAttributeValueRequest,
} from "../../../../domain/types/company/company-product-system";
import { companyProductSystemUseCase } from "../../../../di/container";
import { ReorderError, SortableItem, SortableList, useInstantReorder } from "./sortable";
import {
  BINDING_TYPE_LABELS,
  BINDING_DIRECTION_LABELS,
  END_PAPER_PLACEMENT_LABELS,
  getValueTypeLabel,
} from "./option-constants";

interface Props {
  attribute: OptionAttributeDto;
  option: OptionDto;
  materials: MaterialDto[];
  companyId: number;
  productId: number;
  onRefresh: () => Promise<void>;
}

// 다이얼로그 컴포넌트
const AddValueDialog: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}> = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
    <div className={`relative bg-white rounded-[16px] shadow-xl p-6 min-w-[320px] ${wide ? "max-w-[760px]" : "max-w-[450px]"} mx-4 max-h-[80vh] overflow-y-auto`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-bold text-[#333]">{title}</h3>
        <button
          onClick={onClose}
          className="w-[28px] h-[28px] flex items-center justify-center rounded-full hover:bg-[#f5f5f5] text-[#999] hover:text-[#555] transition-colors"
        >
          <X className="w-[16px] h-[16px]" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

// 값에서 색상 코드 추출 (형식: "용지명 / #FFFFFF")
const parseValueWithColor = (value: string | null | undefined) => {
  if (!value) return { name: "용지", colorCode: null };
  const parts = value.split(" / ");
  if (parts.length === 2 && /^#[0-9A-Fa-f]{6}$/.test(parts[1])) {
    return { name: parts[0], colorCode: parts[1] };
  }
  return { name: value, colorCode: null };
};

const tierInputStyle =
  "w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] text-[#333] placeholder-[#bbb] focus:border-[#4285F4] focus:outline-none transition-colors";

/**값 카드에 표시할 금액 요약 문구.
 *
 * 금액은 수량 구간으로 관리되므로 구간이 둘 이상이면 구간 수를 보여 주고,
 * 하나뿐이면 그 금액(= 기본 금액)을 그대로 보여 준다.
 */
const getValuePriceLabel = (val: OptionAttributeValueDto): string | null => {
  const stepCount = new Set((val.quantityPriceTiers || []).map((t) => t.minQuantity)).size;

  if (val.priceMode === "SPINE_TIER" && val.spinePriceTiers && val.spinePriceTiers.length > 0) {
    return stepCount > 1
      ? `책등 두께별 ${val.spinePriceTiers.length}구간 × 수량별 ${stepCount}구간`
      : `책등 두께별 ${val.spinePriceTiers.length}구간`;
  }
  if (stepCount > 1) return `수량별 ${stepCount}구간`;
  if (val.price != null && val.price > 0) return `+${val.price.toLocaleString()}원`;
  return null;
};

/**
 * 값 카드 격자
 *
 * 카드 폭을 고정하면 오른쪽에 자투리가 크게 남으므로, 최소 폭만 정해 두고 남는 폭은 카드들이 나눠 갖는다.
 * 창이 넓어져 최소 폭짜리 카드가 하나 더 들어갈 수 있게 되면 열이 자동으로 늘어난다.
 */
const VALUE_GRID = "grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2";

/** 값 카드와 추가 버튼이 같은 격자 칸을 차지하도록 크기를 한 곳에서 관리한다 */
const VALUE_CARD_SIZE = "w-full min-h-[66px]";

/**
 * 옵션 값 카드
 *
 * 카드를 누르면 바로 수정 다이얼로그가 열리므로 별도의 수정 버튼을 두지 않는다.
 * 삭제만 우상단에 두어 카드를 누르다 실수로 지우는 일이 없게 분리했다.
 */
const ValueCard: React.FC<{
  label: string;
  priceLabel?: string | null;
  isDisabled?: boolean;
  /** 이미지 선택형 속성일 때만 넘어온다. 없으면 지금까지처럼 글자만 있는 카드다. */
  imageUrl?: string | null;
  onEdit: () => void;
  onRemove?: () => void;
  removeTitle?: string;
}> = ({ label, priceLabel, isDisabled, imageUrl, onEdit, onRemove, removeTitle }) => (
  <div
    onClick={onEdit}
    title={isDisabled ? "클릭해서 수정 (활성화 가능)" : "클릭해서 수정"}
    className={`${VALUE_CARD_SIZE} relative p-3 pr-8 rounded-[12px] border text-left cursor-pointer transition-colors ${
      isDisabled
        ? "bg-[#fafafa] border-dashed border-[#ddd] hover:border-[#4285F4]"
        : "bg-white border-[#e0e0e0] hover:border-[#4285F4]"
    }`}
  >
    {imageUrl && (
      <div className="mb-2 w-full aspect-square overflow-hidden rounded-[8px] border border-[#eee] bg-white">
        <img
          src={imageUrl}
          alt={label}
          className={`w-full h-full object-cover ${isDisabled ? "opacity-50" : ""}`}
        />
      </div>
    )}
    <p
      className={`text-[13px] font-medium leading-snug break-all ${
        isDisabled ? "text-[#aaa]" : "text-[#222]"
      }`}
    >
      {label}
    </p>
    <p className="mt-1 text-[12px]">
      {isDisabled ? (
        <span className="text-[#F57C00]">비활성화</span>
      ) : priceLabel ? (
        <span className="font-medium text-[#4285F4]">{priceLabel}</span>
      ) : (
        <span className="text-[#bbb]">금액 미설정</span>
      )}
    </p>

    {onRemove && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute top-1.5 right-1.5 w-[22px] h-[22px] flex items-center justify-center rounded-full text-[#ccc] hover:text-[#F24A4A] hover:bg-[#FEF2F2] transition-colors"
        title={removeTitle || "삭제"}
      >
        <X className="w-[13px] h-[13px]" />
      </button>
    )}
  </div>
);

/**값 추가 카드 — 값 카드와 같은 격자를 차지하는 점선 카드*/
const AddValueCard: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label }) => (
  <button
    onClick={onClick}
    className={`${VALUE_CARD_SIZE} flex flex-col items-center justify-center gap-1 rounded-[12px] border-2 border-dashed border-[#ddd] text-[13px] font-medium text-[#bbb] hover:border-[#4285F4] hover:text-[#4285F4] hover:bg-[#fafafa] transition-colors`}
  >
    <Plus className="w-[18px] h-[18px]" />
    {label || "추가"}
  </button>
);

/**
 * 수정 다이얼로그 하단 액션
 *
 * 삭제는 왼쪽 끝에, 취소·저장은 오른쪽에 둔다.
 * 파괴적인 동작과 일반 동작을 양끝으로 갈라 놓아야 잘못 누르는 일이 줄어든다.
 */
const EditDialogFooter: React.FC<{
  onDelete?: () => void;
  deleteLabel?: string;
  onCancel: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
}> = ({ onDelete, deleteLabel, onCancel, onSave, saveDisabled }) => (
  <div className="flex items-center justify-between gap-2">
    {onDelete ? (
      <button
        onClick={onDelete}
        className="inline-flex items-center gap-1 px-3 py-2 rounded-[8px] text-[13px] font-medium text-[#F24A4A] hover:bg-[#FEF2F2] transition-colors"
      >
        <X className="w-[13px] h-[13px]" />
        {deleteLabel || "삭제"}
      </button>
    ) : (
      <span />
    )}
    <div className="flex gap-2">
      <button onClick={onCancel} className="px-4 py-2 text-[13px] text-[#666] hover:text-[#333]">
        취소
      </button>
      <button
        onClick={onSave}
        disabled={saveDisabled}
        className="px-4 py-2 bg-[#222] text-white text-[13px] rounded-[8px] disabled:bg-[#e0e0e0] disabled:text-[#aaa]"
      >
        저장
      </button>
    </div>
  </div>
);

/**
 * 옵션 값 이미지 입력 (수정 다이얼로그용)
 *
 * 클릭하면 파일을 고르고, onRemove 가 넘어오면 등록된 이미지를 지울 수 있다.
 * 이미지 선택형 속성처럼 이미지가 필수인 곳에서는 onRemove 를 넘기지 않는다.
 */
const ValueImageField: React.FC<{
  previewUrl: string;
  fileName?: string;
  onSelect: (file: File) => void;
  onRemove?: () => void;
  helpText: string;
}> = ({ previewUrl, fileName, onSelect, onRemove, helpText }) => (
  <div className="mb-4">
    <label className="block text-[13px] font-medium text-[#555] mb-2">옵션 이미지</label>
    <div className="relative">
      <label className="flex min-h-[112px] cursor-pointer items-center justify-center rounded-[8px] border-2 border-dashed border-[#d0d0d0] bg-[#fafafa] p-3 hover:border-[#4285F4] transition-colors">
        {previewUrl ? (
          <div className="flex items-center gap-3">
            <div className="w-[84px] h-[84px] overflow-hidden rounded-[6px] border border-[#e0e0e0] bg-white">
              <img src={previewUrl} alt="옵션 이미지 미리보기" className="w-full h-full object-cover" />
            </div>
            <span className="max-w-[210px] truncate text-[12px] text-[#555]">
              {fileName || "클릭하여 이미지 변경"}
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-[#888]">
            <Upload className="w-5 h-5" />
            <span className="text-[12px]">클릭하여 이미지 첨부</span>
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            onSelect(file);
            // 같은 파일을 지웠다가 다시 고를 수 있도록 입력값을 비운다
            e.target.value = "";
          }}
        />
      </label>
      {previewUrl && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-[6px] bg-white border border-[#e0e0e0] text-[11px] text-[#888] hover:text-[#F24A4A] hover:border-[#F24A4A] transition-colors"
        >
          <X className="w-[11px] h-[11px]" />
          이미지 삭제
        </button>
      )}
    </div>
    <p className="text-[11px] text-[#999] mt-1">{helpText}</p>
  </div>
);

/**
 * 옵션 값 금액 편집기
 *
 * 옵션 값의 금액은 전부 수량 구간으로 관리한다.
 * 맨 앞의 "1개~" 구간이 곧 기본 금액이고, 그보다 큰 구간을 덧붙이면
 * 주문 수량이 그 구간에 도달했을 때 금액이 대신 적용된다.
 *
 * - 기본 금액만 쓰는 옵션은 "1개~" 한 줄짜리 표가 된다.
 * - 책등 두께별 비용을 쓰는 제본 방식은 (책등 두께 × 수량) 형태의 표가 된다.
 */
const ValuePriceEditor: React.FC<{
  steps: string[];
  onStepChange: (index: number, value: string) => void;
  onAddStep: () => void;
  onRemoveStep: (index: number) => void;
  isMatrix: boolean;
  fixedPrices: string[];
  onFixedPriceChange: (index: number, value: string) => void;
  spineRows: { maxSpineThickness: string; price: string }[];
  spineCellPrices: string[][];
  onSpineCellPriceChange: (rowIndex: number, colIndex: number, value: string) => void;
  hasDuplicateStep: boolean;
}> = ({
  steps,
  onStepChange,
  onAddStep,
  onRemoveStep,
  isMatrix,
  fixedPrices,
  onFixedPriceChange,
  spineRows,
  spineCellPrices,
  onSpineCellPriceChange,
  hasDuplicateStep,
}) => (
  <div className="mb-4 border-t border-[#e8e8e8] pt-4">
    <label className="block text-[13px] font-medium text-[#555] mb-1">금액 설정</label>
    <p className="text-[11px] text-[#999] mb-3">
      맨 앞 <span className="font-medium text-[#4285F4]">1개~ 구간이 기본 금액</span>입니다.
      구간을 더하면 주문 수량이 그 구간에 도달했을 때 해당 금액이 적용됩니다.
    </p>

    <div>
      {isMatrix ? (
        /* 책등 두께 × 수량 매트릭스 — 수량 구간은 열 머리에서 바로 편집한다 */
        <div className="overflow-x-auto border border-[#eee] rounded-[8px]">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#fafafa]">
                <th className="text-[12px] font-medium text-[#555] text-left px-3 py-2 border-b border-[#eee] whitespace-nowrap">
                  책등 두께
                </th>
                {steps.map((step, idx) => (
                  <th key={idx} className="px-1.5 py-1.5 border-b border-[#eee] whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <div className="relative w-[86px]">
                        <input
                          type="text"
                          className={
                            tierInputStyle +
                            " pr-8 !py-1.5" +
                            (idx === 0 ? " bg-[#f0f0f0] text-[#888]" : "")
                          }
                          value={step}
                          onChange={(e) => onStepChange(idx, e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder="1"
                          readOnly={idx === 0}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-[#888]">개~</span>
                      </div>
                      {idx > 0 && (
                        <button
                          onClick={() => onRemoveStep(idx)}
                          className="w-[18px] h-[18px] flex items-center justify-center rounded-full text-[#ccc] hover:text-[#F24A4A] flex-shrink-0"
                          title="구간 삭제"
                        >
                          <X className="w-[11px] h-[11px]" />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {spineRows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  <td className="text-[12px] text-[#333] px-3 py-2 border-b border-[#f5f5f5] whitespace-nowrap">
                    ~{row.maxSpineThickness || "?"}mm
                  </td>
                  {steps.map((_, colIdx) => (
                    <td key={colIdx} className="px-1.5 py-1.5 border-b border-[#f5f5f5]">
                      <div className="relative w-[92px]">
                        <input
                          type="text"
                          className={tierInputStyle + " pr-7 !py-1.5"}
                          value={spineCellPrices[rowIdx]?.[colIdx] ?? ""}
                          onChange={(e) =>
                            onSpineCellPriceChange(rowIdx, colIdx, e.target.value.replace(/[^0-9]/g, ""))
                          }
                          placeholder="0"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-[#888]">원</span>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* 한 구간을 한 행에서 관리한다: [수량]개~ [금액]원 */
        <div className="space-y-2">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="relative w-[104px] flex-shrink-0">
                <input
                  type="text"
                  className={tierInputStyle + " pr-8" + (idx === 0 ? " bg-[#f5f5f5] text-[#888]" : "")}
                  value={step}
                  onChange={(e) => onStepChange(idx, e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="1"
                  readOnly={idx === 0}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#888]">개~</span>
              </div>
              <div className="relative flex-1">
                <input
                  type="text"
                  className={tierInputStyle + " pr-8"}
                  value={fixedPrices[idx] ?? ""}
                  onChange={(e) => onFixedPriceChange(idx, e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#888]">원</span>
              </div>
              {/* 기본 금액 행은 지울 수 없어 자리만 비워 둔다 */}
              {idx > 0 ? (
                <button
                  onClick={() => onRemoveStep(idx)}
                  className="w-[24px] h-[24px] flex items-center justify-center rounded-full text-[#bbb] hover:text-[#F24A4A] hover:bg-[#f5f5f5] flex-shrink-0"
                  title="구간 삭제"
                >
                  <X className="w-[13px] h-[13px]" />
                </button>
              ) : (
                <span className="w-[24px] flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onAddStep}
        className="flex w-full items-center justify-center gap-1 px-3 py-2 mt-2 border-2 border-dashed border-[#ddd] rounded-[8px] text-[12px] font-medium text-[#bbb] hover:border-[#4285F4] hover:text-[#4285F4] transition-colors"
      >
        <Plus className="w-[12px] h-[12px]" />
        수량 구간 추가
      </button>

      {hasDuplicateStep && (
        <p className="text-[11px] text-[#F24A4A] mt-2">중복된 수량 구간이 있습니다.</p>
      )}

      <p className="text-[11px] text-[#999] mt-2">
        예: 1개~ 1,000원 / 10개~ 900원 / 30개~ 600원 (30개 이상이면 600원, 10~29개는 900원)
      </p>
      <p className="text-[11px] text-[#999] mt-1">
        금액을 비워 둔 구간은 저장되지 않습니다. 기본 금액을 비워 두면 0원으로 저장됩니다.
      </p>
    </div>
  </div>
);

export const OptionAttributeRenderer: React.FC<Props> = ({
  attribute,
  option,
  materials,
  companyId,
  productId,
  onRefresh,
}) => {
  const [showAddValue, setShowAddValue] = useState(false);
  const [newValueText, setNewValueText] = useState("");
  const [newValueImageFile, setNewValueImageFile] = useState<File | null>(null);
  const [newValueImagePreviewUrl, setNewValueImagePreviewUrl] = useState("");
  const [newPaperColor, setNewPaperColor] = useState("#FFFFFF"); // hex color code
  const [selectedType, setSelectedType] = useState("");
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>([]);
  const [showMaterialSelect, setShowMaterialSelect] = useState(false);
  const [saving, setSaving] = useState(false);

  // 수정 관련 상태
  const [showEditValue, setShowEditValue] = useState(false);
  const [editingValue, setEditingValue] = useState<OptionAttributeValueDto | null>(null);
  const [editValueText, setEditValueText] = useState("");
  const [editValueImageFile, setEditValueImageFile] = useState<File | null>(null);
  const [editValueImagePreviewUrl, setEditValueImagePreviewUrl] = useState("");
  // 등록돼 있던 이미지를 지우기로 했는지 (새 파일을 고르면 다시 false)
  const [editValueImageRemoved, setEditValueImageRemoved] = useState(false);
  // 간지/면지 용지 종류의 색상 코드 (값에 "용지명 / #FFFFFF" 형식으로 붙어 있던 경우만)
  const [editPaperColor, setEditPaperColor] = useState("");
  const [editValueEnabled, setEditValueEnabled] = useState(true);
  // 가격 결정 방식 (제본 방식 전용): FIXED=고정 비용, SPINE_TIER=책등 두께 구간별 비용
  const [editPriceMode, setEditPriceMode] = useState<"FIXED" | "SPINE_TIER">("FIXED");
  // 책등 두께 구간 (제본 방식 전용, mm 단위 상한). 금액은 수량 구간 표에서 관리한다.
  const [editSpineTiers, setEditSpineTiers] = useState<{ maxSpineThickness: string; price: string }[]>([]);
  // 수량 구간 하한 목록 (표의 열). 항상 "1"로 시작하며 그 열이 기본 금액이다.
  const [quantitySteps, setQuantitySteps] = useState<string[]>(["1"]);
  // 고정 금액 기준일 때의 구간별 금액 (quantitySteps와 인덱스 대응)
  const [fixedQuantityPrices, setFixedQuantityPrices] = useState<string[]>([""]);
  // 책등 두께 기준일 때의 셀 금액 ([책등 두께 구간 인덱스][수량 구간 인덱스])
  const [spineQuantityPrices, setSpineQuantityPrices] = useState<string[][]>([]);

  const sysType = attribute.systemAttributeType;

  // 값 순서: 이 항목(속성) 안에서만 바꿀 수 있다. 숨김·비활성 값까지 포함한 전체 순서를 저장한다
  const valueReorder = useInstantReorder(
    [...attribute.values]
      .sort((a, b) => a.displayOrder - b.displayOrder || a.id - b.id)
      .map((v) => v.id),
    (orderedIds) => companyProductSystemUseCase.reorderOptionItems(
      companyId, productId, { level: "value", optionId: option.id, attributeId: attribute.id }, orderedIds,
    ),
  );
  const valueById = new Map(attribute.values.map((v) => [v.id, v]));
  const orderedAttributeValues = valueReorder.ids
    .map((id) => valueById.get(id))
    .filter((v): v is OptionAttributeValueDto => !!v);

  /**
   * 값 목록을 끌어서 순서를 바꿀 수 있게 감싼다. 핸들은 카드 좌상단에 겹쳐 두어(삭제 X 는 우상단)
   * 카드 클릭(수정)과 겹치지 않는다. 화면에 일부만 보여도 전체 순서 안에서 옮기므로 보이는 순서가 그대로 저장된다.
   */
  const sortableValues = <T extends OptionAttributeValueDto>(
    values: T[],
    group: string,
    render: (val: T) => React.ReactNode,
  ) => {
    if (values.length < 2) {
      return values.map((val) => <React.Fragment key={val.id}>{render(val)}</React.Fragment>);
    }
    const scope = `value-${attribute.id}-${group}`;
    return (
      <SortableList scope={scope} ids={values.map((v) => v.id)} onMove={valueReorder.move} layout="grid">
        {values.map((val) => (
          <SortableItem key={val.id} scope={scope} id={val.id} label={`'${val.value || getValueTypeLabel(val.type)}' 순서 이동`} handleSize="sm">
            {({ setNodeRef, style, handle }) => (
              <div ref={setNodeRef} style={style} className="relative group/sort">
                {render(val)}
                <div className="absolute -top-2 -left-2 z-10 bg-white rounded-full border border-[#E0E0E0] shadow-sm opacity-0 group-hover/sort:opacity-100 focus-within:opacity-100 transition-opacity">
                  {handle}
                </div>
              </div>
            )}
          </SortableItem>
        ))}
      </SortableList>
    );
  };

  // 기본값 변경. 서버가 속성의 defaultAttributeValueId 를 단일 기준으로 들고 있으므로,
  // 관리자 화면의 "기본" 표시와 쇼핑몰의 초기 선택이 항상 같은 값을 가리킨다.
  const handleChangeDefaultValue = async (valueId: number) => {
    if (attribute.defaultAttributeValueId === valueId) return;
    setSaving(true);
    try {
      await companyProductSystemUseCase.updateOptionAttribute(
        companyId,
        productId,
        option.id,
        attribute.id,
        { name: attribute.name, defaultAttributeValueId: valueId },
      );
      await onRefresh();
    } catch {
      alert("기본값 변경에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // 성공 여부를 돌려주어, 다이얼로그에서 호출했을 때 실패·취소 시에는 다이얼로그를 닫지 않게 한다
  const handleDeleteValue = async (valueId: number): Promise<boolean> => {
    if (!window.confirm("이 값을 삭제하시겠습니까?")) return false;
    try {
      await companyProductSystemUseCase.deleteOptionAttributeValue(
        companyId,
        productId,
        option.id,
        attribute.id,
        valueId,
      );
      await onRefresh();
      return true;
    } catch {
      alert("값 삭제에 실패했습니다.");
      return false;
    }
  };

  // 시스템 속성 값 비활성화 (UI에서는 삭제처럼 보임)
  const handleDisableValue = async (val: OptionAttributeValueDto): Promise<boolean> => {
    try {
      await companyProductSystemUseCase.updateOptionAttributeValue(
        companyId,
        productId,
        option.id,
        attribute.id,
        val.id,
        { isEnabled: false },
      );
      await onRefresh();
      return true;
    } catch {
      alert("삭제에 실패했습니다.");
      return false;
    }
  };

  // 시스템 속성 값 활성화 (UI에서는 추가처럼 보임)
  const handleEnableValue = async (val: OptionAttributeValueDto) => {
    try {
      await companyProductSystemUseCase.updateOptionAttributeValue(
        companyId,
        productId,
        option.id,
        attribute.id,
        val.id,
        { isEnabled: true },
      );
      await onRefresh();
      setShowAddValue(false);
    } catch {
      alert("추가에 실패했습니다.");
    }
  };

  // 값 수정 다이얼로그 열기
  const openEditDialog = (val: OptionAttributeValueDto) => {
    setEditingValue(val);
    if (sysType === "PAPER_TYPE") {
      // 용지 이름과 색상 코드를 나눠서 편집하고, 저장할 때 다시 합친다
      const { name, colorCode } = parseValueWithColor(val.value);
      setEditValueText(val.value ? name : "");
      setEditPaperColor(colorCode || "");
    } else {
      setEditValueText(val.value || "");
      setEditPaperColor("");
    }
    setEditValueImageFile(null);
    setEditValueImagePreviewUrl(val.imageUrl || "");
    setEditValueImageRemoved(false);
    setEditValueEnabled(val.isEnabled);
    setEditPriceMode(val.priceMode === "SPINE_TIER" ? "SPINE_TIER" : "FIXED");
    const spineTiers = (val.spinePriceTiers || []).map((t) => ({
      maxSpineThickness: String(t.maxSpineThickness),
      price: String(t.price),
    }));
    setEditSpineTiers(spineTiers);

    // 저장된 수량 구간을 (수량 하한 = 열) 기준으로 펼쳐 표 형태로 되돌린다.
    // 1개~ 열은 기본 금액이므로 저장된 구간이 없어도 항상 만들어 둔다.
    const quantityTiers = val.quantityPriceTiers || [];
    const steps = Array.from(new Set([1, ...quantityTiers.map((t) => t.minQuantity)])).sort((a, b) => a - b);
    setQuantitySteps(steps.map(String));
    setFixedQuantityPrices(
      steps.map((step) => {
        const found = quantityTiers.find((t) => t.minQuantity === step && t.maxSpineThickness == null);
        if (found) return String(found.price);
        // 아직 구간으로 옮겨지지 않은 예전 데이터는 기존 금액을 기본 금액으로 보여 준다
        if (step === 1 && val.price != null) return String(val.price);
        return "";
      }),
    );
    setSpineQuantityPrices(
      spineTiers.map((row) =>
        steps.map((step) => {
          const found = quantityTiers.find(
            (t) => t.minQuantity === step && Number(t.maxSpineThickness) === Number(row.maxSpineThickness),
          );
          if (found) return String(found.price);
          if (step === 1 && row.price) return row.price;
          return "";
        }),
      ),
    );
    setShowEditValue(true);
  };

  // 값 수정 다이얼로그 닫기
  const closeEditDialog = () => {
    setShowEditValue(false);
    setEditingValue(null);
    setEditValueText("");
    setEditValueImageFile(null);
    setEditValueImagePreviewUrl("");
    setEditValueImageRemoved(false);
    setEditPaperColor("");
    setEditValueEnabled(true);
    setEditPriceMode("FIXED");
    setEditSpineTiers([]);
    setQuantitySteps(["1"]);
    setFixedQuantityPrices([""]);
    setSpineQuantityPrices([]);
  };

  /**수정 다이얼로그 하단의 삭제.
   *
   * 지울 수 없는 시스템 값은 비활성화로 대신하며, 성공했을 때만 다이얼로그를 닫는다.
   */
  const handleDeleteFromEditDialog = async () => {
    if (!editingValue) return;
    const done = editingValue.isDeletable
      ? await handleDeleteValue(editingValue.id)
      : await handleDisableValue(editingValue);
    if (done) closeEditDialog();
  };

  // 이미 비활성화된 시스템 값은 더 지울 것이 없어 삭제를 노출하지 않는다
  const canDeleteEditingValue =
    !!editingValue && (editingValue.isDeletable || editingValue.isEnabled);

  // ===== 수량 구간 편집 헬퍼 =====
  // 수량 구간(열)과 셀 금액은 인덱스로 대응되므로 항상 함께 변경한다.

  const addQuantityStep = () => {
    setQuantitySteps((prev) => [...prev, ""]);
    setFixedQuantityPrices((prev) => [...prev, ""]);
    setSpineQuantityPrices((prev) => prev.map((row) => [...row, ""]));
  };

  // 첫 구간(1개~)은 기본 금액이라 지울 수 없다
  const removeQuantityStep = (index: number) => {
    if (index === 0) return;
    setQuantitySteps((prev) => prev.filter((_, i) => i !== index));
    setFixedQuantityPrices((prev) => prev.filter((_, i) => i !== index));
    setSpineQuantityPrices((prev) => prev.map((row) => row.filter((_, i) => i !== index)));
  };

  const addSpineTierRow = () => {
    setEditSpineTiers((prev) => [...prev, { maxSpineThickness: "", price: "" }]);
    setSpineQuantityPrices((prev) => [...prev, quantitySteps.map(() => "")]);
  };

  const removeSpineTierRow = (index: number) => {
    setEditSpineTiers((prev) => prev.filter((_, i) => i !== index));
    setSpineQuantityPrices((prev) => prev.filter((_, i) => i !== index));
  };

  // 수량 구간 하한 중복 여부
  const hasDuplicateQuantityStep = () => {
    const filled = quantitySteps.filter((s) => s.trim() !== "").map(Number);
    return new Set(filled).size !== filled.length;
  };

  // 입력된 수량 구간을 요청 형태로 변환
  // 금액이 비어있는 구간은 저장하지 않되, 기본 금액인 1개~ 구간만은 0원으로 채운다.
  const buildQuantityTierRequest = () => {
    const result: { maxSpineThickness?: number; minQuantity: number; price: number }[] = [];

    quantitySteps.forEach((step, colIdx) => {
      if (step.trim() === "") return;
      const minQuantity = Number(step);
      const isBaseStep = minQuantity === 1;

      if (editPriceMode === "SPINE_TIER") {
        editSpineTiers.forEach((row, rowIdx) => {
          if (row.maxSpineThickness.trim() === "") return;
          const cell = spineQuantityPrices[rowIdx]?.[colIdx];
          if (!cell || cell.trim() === "") {
            if (!isBaseStep) return;
            result.push({ maxSpineThickness: Number(row.maxSpineThickness), minQuantity, price: 0 });
            return;
          }
          result.push({
            maxSpineThickness: Number(row.maxSpineThickness),
            minQuantity,
            price: Number(cell),
          });
        });
      } else {
        const cell = fixedQuantityPrices[colIdx];
        if (!cell || cell.trim() === "") {
          if (isBaseStep) result.push({ minQuantity, price: 0 });
          return;
        }
        result.push({ minQuantity, price: Number(cell) });
      }
    });

    return result;
  };

  // 금액 편집기를 각 수정 다이얼로그에 동일한 형태로 끼워 넣는다
  const renderValuePriceEditor = () => {
    return (
      <ValuePriceEditor
        steps={quantitySteps}
        onStepChange={(idx, value) =>
          setQuantitySteps((prev) => prev.map((s, i) => (i === idx ? value : s)))
        }
        onAddStep={addQuantityStep}
        onRemoveStep={removeQuantityStep}
        isMatrix={editPriceMode === "SPINE_TIER" && sysType === "BINDING_TYPE"}
        fixedPrices={fixedQuantityPrices}
        onFixedPriceChange={(idx, value) =>
          setFixedQuantityPrices((prev) => prev.map((p, i) => (i === idx ? value : p)))
        }
        spineRows={editSpineTiers}
        spineCellPrices={spineQuantityPrices}
        onSpineCellPriceChange={(rowIdx, colIdx, value) =>
          setSpineQuantityPrices((prev) =>
            prev.map((row, i) => (i === rowIdx ? row.map((c, j) => (j === colIdx ? value : c)) : row)),
          )
        }
        hasDuplicateStep={hasDuplicateQuantityStep()}
      />
    );
  };

  // 입력된 구간 행을 요청 형태로 변환 (상한이 비어있는 행은 제외)
  // 두께 구간의 기본 금액은 금액 표의 1개~ 열에서 가져온다.
  const buildSpineTierRequest = () =>
    editSpineTiers
      .map((tier, rowIdx) => ({ tier, rowIdx }))
      .filter(({ tier }) => tier.maxSpineThickness.trim() !== "")
      .map(({ tier, rowIdx }) => ({
        maxSpineThickness: Number(tier.maxSpineThickness),
        price: Number(spineQuantityPrices[rowIdx]?.[0] || 0),
      }))
      .sort((a, b) => a.maxSpineThickness - b.maxSpineThickness);

  // 구간 상한(mm) 중복 여부
  const hasDuplicateSpineTier = () => {
    const filled = editSpineTiers
      .filter((t) => t.maxSpineThickness.trim() !== "")
      .map((t) => Number(t.maxSpineThickness));
    return new Set(filled).size !== filled.length;
  };

  // 값 수정 저장
  const handleUpdateValue = async () => {
    if (!editingValue) return;
    // 색상 코드가 붙어 있던 용지 종류는 "용지명 / #FFFFFF" 형식으로 다시 합친다
    const valueToSave =
      sysType === "PAPER_TYPE" && editValueText.trim() && /^#[0-9A-Fa-f]{6}$/.test(editPaperColor)
        ? `${editValueText.trim()} / ${editPaperColor}`
        : editValueText;
    setSaving(true);
    try {
      await companyProductSystemUseCase.updateOptionAttributeValue(
        companyId,
        productId,
        option.id,
        attribute.id,
        editingValue.id,
        {
          value: valueToSave || undefined,
          isEnabled: editValueEnabled,
          // 서버는 imageUrl 이 null 이면 기존 이미지를 유지하므로, 지울 때는 빈 문자열을 보낸다
          ...(editValueImageRemoved && !editValueImageFile ? { imageUrl: "" } : {}),
          // 제본 방식만 가격 방식 선택 + 책등 두께 구간을 관리 (빈 배열 = 전체 삭제)
          ...(sysType === "BINDING_TYPE"
            ? { priceMode: editPriceMode, spinePriceTiers: buildSpineTierRequest() }
            : {}),
          // 금액은 전부 수량 구간으로 관리한다 (1개~ 구간이 기본 금액)
          quantityPriceTiers: buildQuantityTierRequest(),
        },
        editValueImageFile || undefined,
      );
      await onRefresh();
      closeEditDialog();
    } catch {
      alert("값 수정에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddValue = async () => {
    setSaving(true);
    try {
      const request: CreateOptionAttributeValueRequest = {
        isEnabled: true,  // 새로 추가하는 값은 기본 활성화
      };
      const isIndependentPaper = option.optionType === "BOOK_INSERT_PAPER" || option.optionType === "BOOK_END_PAPER";

      if (sysType === "BINDING_TYPE" || sysType === "BINDING_DIRECTION" || sysType === "END_PAPER_PLACEMENT") {
        if (!selectedType) return;
        request.type = selectedType as any;
        // 쇼핑몰은 값 이름(value)을 그대로 보여 준다. 타입만 보내면 이름 없는 제본 카드가 생기므로 라벨을 이름으로 함께 보낸다
        if (sysType === "BINDING_TYPE") request.value = BINDING_TYPE_LABELS[selectedType] ?? undefined;
      } else if (sysType === "PAPER_TYPE" || sysType === "GRAMMAGE") {
        // 간지/면지 용지 종류에 색상 정보 포함
        if (isIndependentPaper && sysType === "PAPER_TYPE" && newPaperColor.trim()) {
          request.value = `${newValueText.trim()} / ${newPaperColor.trim()}`;
        } else {
          request.value = newValueText || undefined;
        }
        if (selectedMaterialIds.length > 0) {
          request.materialIds = selectedMaterialIds;
        }
      } else {
        request.value = newValueText || undefined;
        if (selectedMaterialIds.length > 0) {
          request.materialIds = selectedMaterialIds;
        }
      }

      await companyProductSystemUseCase.createOptionAttributeValue(
        companyId,
        productId,
        option.id,
        attribute.id,
        request,
        newValueImageFile || undefined,
      );
      await onRefresh();
      closeDialog();
    } catch {
      alert("값 추가에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const closeDialog = () => {
    setShowAddValue(false);
    setNewValueText("");
    setNewValueImageFile(null);
    setNewValueImagePreviewUrl("");
    setNewPaperColor("#FFFFFF");
    setSelectedType("");
    setSelectedMaterialIds([]);
    setShowMaterialSelect(false);
  };

  // Get existing type values to filter dropdown
  const existingTypes = new Set(
    attribute.values.map((v) => v.type as string | undefined).filter((t): t is string => !!t),
  );

  const inputStyle = "w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[14px] text-[#333] placeholder-[#bbb] focus:border-[#4285F4] focus:outline-none transition-colors";

  // ===== Rendering by systemAttributeType =====

  const renderBindingTypeValues = () => {
    const enabledValues = orderedAttributeValues.filter((v) => v.isEnabled);
    const disabledValues = orderedAttributeValues.filter((v) => !v.isEnabled);

    return (
      <>
        <div className={VALUE_GRID}>
          {sortableValues(enabledValues, "enabled", (val) => (
            <ValueCard
              key={val.id}
              label={getValueTypeLabel(val.type)}
              priceLabel={getValuePriceLabel(val)}
              // 제본 방식은 쇼핑몰에서 이미지 박스로 보여 주므로 미리보기 설정과 무관하게 이미지를 표시한다
              imageUrl={val.imageUrl}
              onEdit={() => openEditDialog(val)}
              onRemove={() => handleDisableValue(val)}
            />
          ))}

          {disabledValues.length > 0 && <AddValueCard onClick={() => setShowAddValue(true)} />}
        </div>

        {showAddValue && disabledValues.length > 0 && (
          <AddValueDialog title="제본 방식 선택" onClose={closeDialog}>
            <p className="text-[13px] text-[#666] mb-4">사용할 제본 방식을 클릭하세요</p>
            <div className="flex flex-wrap gap-2">
              {disabledValues.map((val) => (
                <button
                  key={val.id}
                  onClick={() => handleEnableValue(val)}
                  className="px-4 py-3 bg-[#f5f5f5] border border-[#e0e0e0] rounded-[10px] text-[14px] text-[#555] hover:border-[#4285F4] hover:bg-[#E8EFFF] hover:text-[#4285F4] transition-colors"
                >
                  {getValueTypeLabel(val.type)}
                </button>
              ))}
            </div>
          </AddValueDialog>
        )}

        {showEditValue && editingValue && (
          <AddValueDialog
            title="제본 방식 수정"
            onClose={closeEditDialog}
            wide={editPriceMode === "SPINE_TIER"}
          >
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">제본 방식</label>
              <p className="text-[14px] text-[#333] px-3 py-2 bg-[#f5f5f5] rounded-[8px]">{getValueTypeLabel(editingValue.type)}</p>
            </div>
            <ValueImageField
              previewUrl={editValueImagePreviewUrl}
              fileName={editValueImageFile?.name}
              onSelect={(file) => {
                setEditValueImageFile(file);
                setEditValueImagePreviewUrl(URL.createObjectURL(file));
                setEditValueImageRemoved(false);
              }}
              onRemove={() => {
                setEditValueImageFile(null);
                setEditValueImagePreviewUrl("");
                setEditValueImageRemoved(true);
              }}
              helpText="쇼핑몰의 제본 방식 선택 박스에 표시됩니다. 비워 두면 기본 아이콘이 나옵니다."
            />
            {/* 가격 결정 방식 선택 */}
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">추가 비용 방식</label>
              <div className="flex rounded-[8px] border border-[#e0e0e0] overflow-hidden">
                <button
                  type="button"
                  onClick={() => setEditPriceMode("FIXED")}
                  className={`flex-1 py-2.5 text-[13px] font-medium transition-colors ${
                    editPriceMode === "FIXED"
                      ? "bg-[#E8EFFF] text-[#4285F4]"
                      : "bg-white text-[#888] hover:bg-[#fafafa]"
                  }`}
                >
                  고정 비용
                </button>
                <button
                  type="button"
                  onClick={() => setEditPriceMode("SPINE_TIER")}
                  className={`flex-1 py-2.5 text-[13px] font-medium border-l border-[#e0e0e0] transition-colors ${
                    editPriceMode === "SPINE_TIER"
                      ? "bg-[#E8EFFF] text-[#4285F4]"
                      : "bg-white text-[#888] hover:bg-[#fafafa]"
                  }`}
                >
                  책등 두께별 비용
                </button>
              </div>
              <p className="text-[11px] text-[#999] mt-1">
                {editPriceMode === "FIXED"
                  ? "선택 시 항상 같은 비용이 추가됩니다"
                  : "책등 두께(세네카)에 따라 비용이 달라집니다"}
              </p>
            </div>

            {/* 책등 두께 구간 (금액은 아래 금액 설정 표에서 관리한다) */}
            {editPriceMode === "SPINE_TIER" && (
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-1">책등 두께 구간</label>
              <p className="text-[11px] text-[#999] mb-3">
                책등 두께(세네카)가 입력한 상한 이하이면 해당 구간의 비용이 적용됩니다.
                가장 큰 구간을 초과하는 두께는 <span className="font-medium text-[#4285F4]">가장 큰 구간의 비용이 그대로 적용</span>됩니다.
                구간별 금액은 아래 금액 설정 표에서 입력합니다.
              </p>
              {editSpineTiers.length > 0 && (
                <div className="space-y-2 mb-2">
                  {editSpineTiers.map((tier, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-[12px] text-[#888] flex-shrink-0">~</span>
                      <div className="relative w-[110px] flex-shrink-0">
                        <input
                          type="text"
                          className={inputStyle + " pr-9 !py-2"}
                          value={tier.maxSpineThickness}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9.]/g, "");
                            setEditSpineTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, maxSpineThickness: v } : t)));
                          }}
                          placeholder="20"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[#888]">mm</span>
                      </div>
                      <button
                        onClick={() => removeSpineTierRow(idx)}
                        className="w-[24px] h-[24px] flex items-center justify-center rounded-full text-[#bbb] hover:text-[#F24A4A] hover:bg-[#f5f5f5] flex-shrink-0"
                        title="구간 삭제"
                      >
                        <X className="w-[13px] h-[13px]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={addSpineTierRow}
                className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-dashed border-[#ddd] rounded-[8px] text-[12px] font-medium text-[#bbb] hover:border-[#4285F4] hover:text-[#4285F4] transition-colors"
              >
                <Plus className="w-[12px] h-[12px]" />
                구간 추가
              </button>
              {hasDuplicateSpineTier() && (
                <p className="text-[11px] text-[#F24A4A] mt-2">중복된 책등 두께 구간이 있습니다.</p>
              )}
              <p className="text-[11px] text-[#999] mt-2">예: ~20mm / ~30mm / ~40mm (40mm 초과는 마지막 구간의 금액이 적용)</p>
            </div>
            )}

            {renderValuePriceEditor()}

            <EditDialogFooter
              onDelete={canDeleteEditingValue ? handleDeleteFromEditDialog : undefined}
              onCancel={closeEditDialog}
              onSave={handleUpdateValue}
              saveDisabled={
                saving ||
                (editPriceMode === "SPINE_TIER" &&
                  (hasDuplicateSpineTier() || buildSpineTierRequest().length === 0)) ||
                hasDuplicateQuantityStep()
              }
            />
          </AddValueDialog>
        )}
      </>
    );
  };

  const renderBindingDirectionValues = () => {
    const enabledValues = orderedAttributeValues.filter((v) => v.isEnabled);
    const disabledValues = orderedAttributeValues.filter((v) => !v.isEnabled);

    return (
      <>
        <div className="flex flex-wrap gap-2 items-center">
          {sortableValues(enabledValues, "enabled", (val) => (
            <div
              key={val.id}
              className="group/val relative inline-flex items-center gap-1.5 px-4 py-2 bg-[#E8EFFF] rounded-full cursor-pointer hover:ring-2 hover:ring-[#4285F4]/30 transition-all"
              onClick={() => openEditDialog(val)}
            >
              <span className="text-[13px] font-medium text-[#4285F4]">
                {getValueTypeLabel(val.type)}
              </span>
              {getValuePriceLabel(val) && (
                <span className="text-[11px] text-[#4285F4]/70">{getValuePriceLabel(val)}</span>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleDisableValue(val); }}
                className="text-[#aac] hover:text-[#F24A4A] opacity-0 group-hover/val:opacity-100 transition-opacity"
                title="삭제"
              >
                <X className="w-[12px] h-[12px]" />
              </button>
            </div>
          ))}

          {/* 스켈레톤 스타일 추가 버튼 (pill 형태) */}
          {disabledValues.length > 0 && (
            <button
              onClick={() => setShowAddValue(true)}
              className="inline-flex items-center gap-1 px-4 py-2 bg-[#f8f8f8] border-2 border-dashed border-[#ddd] rounded-full hover:border-[#4285F4] hover:bg-[#fafafa] transition-colors group/add"
            >
              <Plus className="w-[14px] h-[14px] text-[#ccc] group-hover/add:text-[#4285F4] transition-colors" />
              <span className="text-[13px] font-medium text-[#bbb] group-hover/add:text-[#4285F4] transition-colors">
                추가
              </span>
            </button>
          )}
        </div>

        {showAddValue && disabledValues.length > 0 && (
          <AddValueDialog title="제본 방향 선택" onClose={closeDialog}>
            <p className="text-[13px] text-[#666] mb-4">사용할 제본 방향을 클릭하세요</p>
            <div className="flex flex-wrap gap-2">
              {disabledValues.map((val) => (
                <button
                  key={val.id}
                  onClick={() => handleEnableValue(val)}
                  className="px-4 py-3 bg-[#f5f5f5] border border-[#e0e0e0] rounded-full text-[14px] text-[#555] hover:border-[#4285F4] hover:bg-[#E8EFFF] hover:text-[#4285F4] transition-colors"
                >
                  {getValueTypeLabel(val.type)}
                </button>
              ))}
            </div>
          </AddValueDialog>
        )}

        {showEditValue && editingValue && (
          <AddValueDialog title="제본 방향 수정" onClose={closeEditDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">제본 방향</label>
              <p className="text-[14px] text-[#333] px-3 py-2 bg-[#f5f5f5] rounded-[8px]">{getValueTypeLabel(editingValue.type)}</p>
            </div>
            {renderValuePriceEditor()}
            <EditDialogFooter
              onDelete={canDeleteEditingValue ? handleDeleteFromEditDialog : undefined}
              onCancel={closeEditDialog}
              onSave={handleUpdateValue}
              saveDisabled={saving || hasDuplicateQuantityStep()}
            />
          </AddValueDialog>
        )}
      </>
    );
  };

  const renderPaperOrientationValues = () => {
    // 세로/가로는 시스템이 정한 두 가지뿐이라 추가/삭제 없이 금액만 손볼 수 있게 한다.
    const orderedValues = orderedAttributeValues;

    return (
      <>
        <div className="flex flex-wrap gap-2 items-center">
          {sortableValues(orderedValues, "all", (val) => (
            <div
              key={val.id}
              className="group/val relative inline-flex items-center gap-1.5 px-4 py-2 bg-[#E8EFFF] rounded-full cursor-pointer hover:ring-2 hover:ring-[#4285F4]/30 transition-all"
              onClick={() => openEditDialog(val)}
            >
              <span className="text-[13px] font-medium text-[#4285F4]">
                {getValueTypeLabel(val.type)}
              </span>
              {attribute.defaultAttributeValueId === val.id && (
                <span className="text-[11px] text-[#4285F4]/70">기본</span>
              )}
              {getValuePriceLabel(val) && (
                <span className="text-[11px] text-[#4285F4]/70">{getValuePriceLabel(val)}</span>
              )}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#888] mt-2">
          세로는 등록한 용지 사이즈를 그대로 쓰고, 가로는 가로/세로 길이를 서로 바꿔 계산합니다.
        </p>

        {showEditValue && editingValue && (
          <AddValueDialog title="용지 방향 수정" onClose={closeEditDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">용지 방향</label>
              <p className="text-[14px] text-[#333] px-3 py-2 bg-[#f5f5f5] rounded-[8px]">{getValueTypeLabel(editingValue.type)}</p>
            </div>
            {renderValuePriceEditor()}
            <EditDialogFooter
              onCancel={closeEditDialog}
              onSave={handleUpdateValue}
              saveDisabled={saving || hasDuplicateQuantityStep()}
            />
          </AddValueDialog>
        )}
      </>
    );
  };

  const renderEndPaperPlacementValues = () => {
    const remaining = Object.entries(END_PAPER_PLACEMENT_LABELS).filter(
      ([key]) => !existingTypes.has(key),
    );
    return (
      <>
        <div className="flex flex-wrap gap-3 items-center">
          {sortableValues(orderedAttributeValues, "all", (val) => (
            <div key={val.id} className="group/val relative cursor-pointer" onClick={() => openEditDialog(val)}>
              <div className="px-4 py-3 bg-[#f5f5f5] rounded-[10px] border border-[#e8e8e8] hover:ring-2 hover:ring-[#4285F4]/30 transition-all">
                <span className="text-[13px] font-medium text-[#333]">
                  {getValueTypeLabel(val.type)}
                </span>
              </div>
              {getValuePriceLabel(val) && (
                <p className="text-[11px] text-[#4285F4] text-center mt-1">{getValuePriceLabel(val)}</p>
              )}
              {val.isDeletable && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteValue(val.id); }}
                  className="absolute -top-1.5 -right-1.5 w-[20px] h-[20px] flex items-center justify-center bg-white rounded-full border border-[#e0e0e0] opacity-0 group-hover/val:opacity-100 transition-opacity text-[#bbb] hover:text-[#F24A4A]"
                >
                  <X className="w-[10px] h-[10px]" />
                </button>
              )}
            </div>
          ))}

          {/* 스켈레톤 스타일 추가 버튼 */}
          {attribute.isAddable && remaining.length > 0 && (
            <button
              onClick={() => setShowAddValue(true)}
              className="px-4 py-3 bg-white border-2 border-dashed border-[#ddd] rounded-[10px] hover:border-[#4285F4] transition-colors group/add flex items-center gap-1"
            >
              <Plus className="w-[14px] h-[14px] text-[#ccc] group-hover/add:text-[#4285F4] transition-colors" />
              <span className="text-[13px] font-medium text-[#bbb] group-hover/add:text-[#4285F4] transition-colors">
                추가
              </span>
            </button>
          )}
        </div>

        {showAddValue && (
          <AddValueDialog title="면지 구성 선택" onClose={closeDialog}>
            <p className="text-[13px] text-[#666] mb-3">면지를 어디에 넣을지 선택하세요</p>
            <select
              className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-3 text-[14px] mb-4"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="">선택해주세요</option>
              {remaining.map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button onClick={closeDialog} className="px-4 py-2 text-[13px] text-[#666] hover:text-[#333]">취소</button>
              <button onClick={handleAddValue} disabled={!selectedType || saving} className="px-4 py-2 bg-[#222] text-white text-[13px] rounded-[8px] disabled:bg-[#e0e0e0] disabled:text-[#aaa]">추가</button>
            </div>
          </AddValueDialog>
        )}

        {showEditValue && editingValue && (
          <AddValueDialog title="면지 구성 수정" onClose={closeEditDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">면지 구성</label>
              <p className="text-[14px] text-[#333] px-3 py-2 bg-[#f5f5f5] rounded-[8px]">{getValueTypeLabel(editingValue.type)}</p>
            </div>
            {renderValuePriceEditor()}
            <EditDialogFooter
              onDelete={canDeleteEditingValue ? handleDeleteFromEditDialog : undefined}
              onCancel={closeEditDialog}
              onSave={handleUpdateValue}
              saveDisabled={saving || hasDuplicateQuantityStep()}
            />
          </AddValueDialog>
        )}
      </>
    );
  };

  const renderPaperTypeValues = () => {
    // 간지와 면지는 자재 연결 없이 직접 입력
    const isIndependentPaper = option.optionType === "BOOK_INSERT_PAPER" || option.optionType === "BOOK_END_PAPER";

    return (
      <>
        <div className="flex flex-wrap gap-3 items-center">
          {sortableValues(orderedAttributeValues, "all", (val) => {
            const { name: matName, colorCode } = parseValueWithColor(val.value);
            return (
              <div key={val.id} className="group/val relative cursor-pointer" onClick={() => openEditDialog(val)}>
                <div className="px-4 py-3 bg-[#f5f5f5] rounded-[10px] border border-[#e0e0e0] flex items-center gap-2 hover:ring-2 hover:ring-[#4285F4]/30 transition-all">
                  {/* 색상 견본 표시 */}
                  {colorCode && (
                    <div
                      className="w-[20px] h-[20px] rounded-[4px] border border-[#d0d0d0] flex-shrink-0"
                      style={{ backgroundColor: colorCode }}
                      title={colorCode}
                    />
                  )}
                  <span className="text-[13px] font-medium text-[#333]">
                    {matName}
                  </span>
                </div>
                {getValuePriceLabel(val) && (
                  <p className="text-[11px] text-[#4285F4] text-center mt-1">{getValuePriceLabel(val)}</p>
                )}
                {val.isDeletable && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteValue(val.id); }}
                    className="absolute -top-1.5 -right-1.5 w-[20px] h-[20px] flex items-center justify-center bg-white rounded-full border border-[#e0e0e0] opacity-0 group-hover/val:opacity-100 transition-opacity text-[#bbb] hover:text-[#F24A4A]"
                  >
                    <X className="w-[10px] h-[10px]" />
                  </button>
                )}
              </div>
            );
          })}

          {/* 스켈레톤 스타일 추가 버튼 */}
          {attribute.isAddable && (
            <button
              onClick={() => setShowAddValue(true)}
              className="px-4 py-3 bg-white border-2 border-dashed border-[#ddd] rounded-[10px] hover:border-[#888] hover:bg-[#f5f5f5] transition-colors group/add flex items-center gap-1"
            >
              <Plus className="w-[14px] h-[14px] text-[#ccc] group-hover/add:text-[#666] transition-colors" />
              <span className="text-[13px] font-medium text-[#bbb] group-hover/add:text-[#666] transition-colors">
                추가
              </span>
            </button>
          )}
        </div>

        {showAddValue && (
          <AddValueDialog title="용지 추가" onClose={closeDialog}>
            {isIndependentPaper ? (
              // 면지/간지: 직접 텍스트 입력 + 색상
              <>
                <div className="mb-4">
                  <label className="block text-[13px] font-medium text-[#555] mb-2">용지 이름 *</label>
                  <input
                    type="text"
                    className={inputStyle}
                    value={newValueText}
                    onChange={(e) => setNewValueText(e.target.value)}
                    placeholder="예: 아트지, 스노우지, 모조지"
                    autoFocus
                  />
                  <p className="text-[11px] text-[#999] mt-1">고객이 선택할 때 보이는 이름입니다</p>
                </div>
                <div className="mb-4">
                  <label className="block text-[13px] font-medium text-[#555] mb-2">종이 색상</label>
                  <p className="text-[11px] text-[#888] mb-2">고객이 셀프 디자인할 때 이 색상이 배경으로 표시됩니다</p>
                  <div className="flex items-center gap-3">
                    {/* Color picker */}
                    <div className="relative">
                      <input
                        type="color"
                        value={newPaperColor}
                        onChange={(e) => setNewPaperColor(e.target.value)}
                        className="w-[48px] h-[48px] rounded-[8px] border border-[#e0e0e0] cursor-pointer p-0 overflow-hidden"
                        style={{ WebkitAppearance: "none" }}
                      />
                    </div>
                    {/* Color value display */}
                    <div className="flex-1">
                      <input
                        type="text"
                        className={inputStyle}
                        value={newPaperColor.toUpperCase()}
                        onChange={(e) => {
                          const val = e.target.value;
                          // hex 코드 형식 검증
                          if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                            setNewPaperColor(val);
                          }
                        }}
                        placeholder="#FFFFFF"
                        maxLength={7}
                      />
                    </div>
                  </div>
                  {/* 미리 정의된 색상 빠른 선택 */}
                  <p className="text-[11px] text-[#888] mt-3 mb-2">자주 쓰는 색상</p>
                  <div className="flex gap-2">
                    {[
                      { color: "#FFFFFF", name: "백색" },
                      { color: "#FFFEF0", name: "미색" },
                      { color: "#FFF8DC", name: "크림" },
                      { color: "#F5F5DC", name: "베이지" },
                      { color: "#E0E0E0", name: "회색" },
                    ].map((preset) => (
                      <button
                        key={preset.color}
                        type="button"
                        onClick={() => setNewPaperColor(preset.color)}
                        className={`flex flex-col items-center gap-1 px-2 py-1.5 rounded-[6px] border transition-colors ${
                          newPaperColor.toUpperCase() === preset.color
                            ? "border-[#4285F4] bg-[#E8EFFF]"
                            : "border-[#e0e0e0] hover:border-[#bbb]"
                        }`}
                        title={preset.name}
                      >
                        <div
                          className="w-[24px] h-[24px] rounded-[4px] border border-[#d0d0d0]"
                          style={{ backgroundColor: preset.color }}
                        />
                        <span className="text-[10px] text-[#666]">{preset.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              // 표지/내지: 자재 선택
              <div className="mb-4">
                <label className="block text-[13px] font-medium text-[#555] mb-2">등록된 자재에서 선택</label>
                <select
                  className={inputStyle}
                  value={selectedMaterialIds[0] || ""}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    setSelectedMaterialIds(id ? [id] : []);
                    const mat = materials.find((m) => m.id === id);
                    if (mat) setNewValueText(mat.name);
                  }}
                >
                  <option value="">용지를 선택하세요</option>
                  {materials
                    .filter((m) => m.type === "SHEET_PAPER" || m.type === "ROLL_PAPER")
                    .map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                </select>
                <p className="text-[11px] text-[#999] mt-1">자재 관리에서 등록한 용지 목록입니다</p>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={closeDialog} className="px-4 py-2 text-[13px] text-[#666] hover:text-[#333]">취소</button>
              <button
                onClick={handleAddValue}
                disabled={saving || (!isIndependentPaper && selectedMaterialIds.length === 0) || (isIndependentPaper && !newValueText.trim())}
                className="px-4 py-2 bg-[#222] text-white text-[13px] rounded-[8px] disabled:bg-[#e0e0e0] disabled:text-[#aaa]"
              >
                추가
              </button>
            </div>
          </AddValueDialog>
        )}

        {/* 수정 다이얼로그 — 간지·면지 용지(노랑 캔트지 등)의 금액을 여기서 입력한다 */}
        {showEditValue && editingValue && (
          <AddValueDialog title="용지 수정" onClose={closeEditDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">용지 이름{isIndependentPaper ? " *" : ""}</label>
              {isIndependentPaper ? (
                <input
                  type="text"
                  className={inputStyle}
                  value={editValueText}
                  onChange={(e) => setEditValueText(e.target.value)}
                  placeholder="예: 아트지, 스노우지, 모조지"
                  autoFocus
                />
              ) : (
                // 표지/내지 용지는 자재 이름을 그대로 쓰므로 이름은 자재 관리에서 바꾼다
                <p className="text-[14px] text-[#333] px-3 py-2 bg-[#f5f5f5] rounded-[8px]">{editValueText || "-"}</p>
              )}
            </div>
            {isIndependentPaper && parseValueWithColor(editingValue.value).colorCode && (
              <div className="mb-4">
                <label className="block text-[13px] font-medium text-[#555] mb-2">종이 색상</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={/^#[0-9A-Fa-f]{6}$/.test(editPaperColor) ? editPaperColor : "#FFFFFF"}
                    onChange={(e) => setEditPaperColor(e.target.value)}
                    className="w-[40px] h-[40px] rounded-[8px] border border-[#e0e0e0] cursor-pointer p-0 overflow-hidden"
                    style={{ WebkitAppearance: "none" }}
                  />
                  <input
                    type="text"
                    className={inputStyle}
                    value={editPaperColor.toUpperCase()}
                    onChange={(e) => {
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) setEditPaperColor(e.target.value);
                    }}
                    placeholder="#FFFFFF"
                    maxLength={7}
                  />
                </div>
              </div>
            )}
            {renderValuePriceEditor()}
            <EditDialogFooter
              onDelete={editingValue.isDeletable ? handleDeleteFromEditDialog : undefined}
              onCancel={closeEditDialog}
              onSave={handleUpdateValue}
              saveDisabled={
                saving ||
                (isIndependentPaper && !editValueText.trim()) ||
                (!!parseValueWithColor(editingValue.value).colorCode && !/^#[0-9A-Fa-f]{6}$/.test(editPaperColor)) ||
                hasDuplicateQuantityStep()
              }
            />
          </AddValueDialog>
        )}
      </>
    );
  };

  const renderGrammageValues = () => {
    return (
      <>
        <div className="flex flex-wrap gap-2 items-center">
          {sortableValues(orderedAttributeValues, "all", (val) => (
            <div
              key={val.id}
              className="group/val relative inline-flex items-center gap-1 px-3 py-1.5 bg-[#f5f5f5] rounded-full border border-[#e0e0e0] cursor-pointer hover:ring-2 hover:ring-[#4285F4]/30 transition-all"
              onClick={() => openEditDialog(val)}
            >
              <span className="text-[13px] font-medium text-[#333]">
                {val.value || "?"}g
              </span>
              {getValuePriceLabel(val) && (
                <span className="text-[11px] text-[#4285F4]">{getValuePriceLabel(val)}</span>
              )}
              {val.isDeletable && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteValue(val.id); }}
                  className="text-[#bbb] hover:text-[#F24A4A] opacity-0 group-hover/val:opacity-100 transition-opacity"
                >
                  <X className="w-[12px] h-[12px]" />
                </button>
              )}
            </div>
          ))}

          {/* 스켈레톤 스타일 추가 버튼 */}
          {attribute.isAddable && (
            <button
              onClick={() => setShowAddValue(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border-2 border-dashed border-[#ddd] rounded-full hover:border-[#888] hover:bg-[#f5f5f5] transition-colors group/add"
            >
              <Plus className="w-[12px] h-[12px] text-[#ccc] group-hover/add:text-[#666] transition-colors" />
              <span className="text-[13px] font-medium text-[#bbb] group-hover/add:text-[#666] transition-colors">
                추가
              </span>
            </button>
          )}
        </div>

        {showAddValue && (
          <AddValueDialog title="용지 두께 추가" onClose={closeDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">평량 (g/㎡) *</label>
              <div className="relative">
                <input
                  type="text"
                  className={inputStyle + " pr-10"}
                  value={newValueText}
                  onChange={(e) => setNewValueText(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="150"
                  autoFocus
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#888]">g</span>
              </div>
              <p className="text-[11px] text-[#999] mt-1">용지의 평량(두께)을 입력하세요 (예: 100, 150, 200)</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeDialog} className="px-4 py-2 text-[13px] text-[#666] hover:text-[#333]">취소</button>
              <button onClick={handleAddValue} disabled={saving || !newValueText.trim()} className="px-4 py-2 bg-[#222] text-white text-[13px] rounded-[8px] disabled:bg-[#e0e0e0] disabled:text-[#aaa]">추가</button>
            </div>
          </AddValueDialog>
        )}

        {showEditValue && editingValue && (
          <AddValueDialog title="용지 두께 수정" onClose={closeEditDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">평량 (g/㎡) *</label>
              <div className="relative">
                <input
                  type="text"
                  className={inputStyle + " pr-10"}
                  value={editValueText}
                  onChange={(e) => setEditValueText(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="150"
                  autoFocus
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#888]">g</span>
              </div>
            </div>
            {renderValuePriceEditor()}
            <EditDialogFooter
              onDelete={editingValue.isDeletable ? handleDeleteFromEditDialog : undefined}
              onCancel={closeEditDialog}
              onSave={handleUpdateValue}
              saveDisabled={saving || !editValueText.trim() || hasDuplicateQuantityStep()}
            />
          </AddValueDialog>
        )}
      </>
    );
  };

  const renderPrintColorModeValues = () => {
    return (
      <>
        <div className="flex flex-wrap gap-3 items-center">
          {sortableValues(orderedAttributeValues, "all", (val) => (
            <div key={val.id} className="group/val relative">
              <div className="px-4 py-3 bg-[#EDE7F6] rounded-[10px] border border-[#D1C4E9]">
                <span className="text-[13px] font-medium text-[#7B1FA2]">
                  {val.value || "인쇄"}
                </span>
              </div>
              {getValuePriceLabel(val) && (
                <p className="text-[11px] text-[#4285F4] text-center mt-1">{getValuePriceLabel(val)}</p>
              )}
              {val.isDeletable && (
                <button
                  onClick={() => handleDeleteValue(val.id)}
                  className="absolute -top-1.5 -right-1.5 w-[20px] h-[20px] flex items-center justify-center bg-white rounded-full border border-[#e0e0e0] opacity-0 group-hover/val:opacity-100 transition-opacity text-[#bbb] hover:text-[#F24A4A]"
                >
                  <X className="w-[10px] h-[10px]" />
                </button>
              )}
            </div>
          ))}

          {/* 스켈레톤 스타일 추가 버튼 */}
          {attribute.isAddable && (
            <button
              onClick={() => setShowAddValue(true)}
              className="px-4 py-3 bg-white border-2 border-dashed border-[#D1C4E9] rounded-[10px] hover:border-[#7B1FA2] hover:bg-[#EDE7F6] transition-colors group/add flex items-center gap-1"
            >
              <Plus className="w-[14px] h-[14px] text-[#D1C4E9] group-hover/add:text-[#7B1FA2] transition-colors" />
              <span className="text-[13px] font-medium text-[#D1C4E9] group-hover/add:text-[#7B1FA2] transition-colors">
                추가
              </span>
            </button>
          )}
        </div>

        {showAddValue && (
          <AddValueDialog title="인쇄 방식 추가" onClose={closeDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">인쇄 방식 *</label>
              <input
                type="text"
                className={inputStyle}
                value={newValueText}
                onChange={(e) => setNewValueText(e.target.value)}
                placeholder="예: 4도 인쇄, 1도 인쇄, 양면 4도"
                autoFocus
              />
              <p className="text-[11px] text-[#999] mt-1">인쇄 색상 수나 방식을 입력하세요</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeDialog} className="px-4 py-2 text-[13px] text-[#666] hover:text-[#333]">취소</button>
              <button onClick={handleAddValue} disabled={saving || !newValueText.trim()} className="px-4 py-2 bg-[#222] text-white text-[13px] rounded-[8px] disabled:bg-[#e0e0e0] disabled:text-[#aaa]">추가</button>
            </div>
          </AddValueDialog>
        )}
      </>
    );
  };

  const renderInsertPageNumberValues = () => {
    return (
      <>
        <div className="flex flex-wrap gap-3 items-center">
          {attribute.values.map((val) => (
            <div key={val.id} className="group/val relative inline-flex items-center gap-1.5 px-3 py-2 bg-[#f5f5f5] rounded-[8px] cursor-pointer hover:ring-2 hover:ring-[#4285F4]/30 transition-all" onClick={() => openEditDialog(val)}>
              <span className="text-[13px] font-medium text-[#333]">
                {val.value || "?"}페이지
              </span>
              {getValuePriceLabel(val) && (
                <span className="text-[11px] text-[#4285F4]">{getValuePriceLabel(val)}</span>
              )}
              {val.isDeletable && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteValue(val.id); }}
                  className="text-[#ccc] hover:text-[#F24A4A] opacity-0 group-hover/val:opacity-100 transition-opacity"
                >
                  <X className="w-[12px] h-[12px]" />
                </button>
              )}
            </div>
          ))}

          {/* 스켈레톤 스타일 추가 버튼 */}
          {attribute.isAddable && (
            <button
              onClick={() => setShowAddValue(true)}
              className="inline-flex items-center gap-1 px-3 py-2 bg-white border-2 border-dashed border-[#ddd] rounded-[8px] hover:border-[#4285F4] hover:bg-[#fafafa] transition-colors group/add"
            >
              <Plus className="w-[14px] h-[14px] text-[#ccc] group-hover/add:text-[#4285F4] transition-colors" />
              <span className="text-[13px] font-medium text-[#bbb] group-hover/add:text-[#4285F4] transition-colors">
                추가
              </span>
            </button>
          )}
        </div>

        {showAddValue && (
          <AddValueDialog title="간지 삽입 위치 추가" onClose={closeDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">페이지 번호 *</label>
              <div className="relative">
                <input
                  type="number"
                  className={inputStyle + " pr-12"}
                  value={newValueText}
                  onChange={(e) => setNewValueText(e.target.value)}
                  placeholder="3"
                  min={1}
                  step={2}
                  autoFocus
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#888]">페이지</span>
              </div>
              <p className="text-[11px] text-[#999] mt-1">간지가 삽입될 위치입니다 (홀수만 가능)</p>
              <p className="text-[11px] text-[#888] mt-0.5">예: 3페이지 = 3페이지 앞에 간지 삽입</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeDialog} className="px-4 py-2 text-[13px] text-[#666] hover:text-[#333]">취소</button>
              <button
                onClick={handleAddValue}
                disabled={!newValueText || Number(newValueText) % 2 === 0 || saving}
                className="px-4 py-2 bg-[#222] text-white text-[13px] rounded-[8px] disabled:bg-[#e0e0e0] disabled:text-[#aaa]"
              >
                추가
              </button>
            </div>
          </AddValueDialog>
        )}

        {showEditValue && editingValue && (
          <AddValueDialog title="간지 삽입 위치 수정" onClose={closeEditDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">페이지 번호 *</label>
              <div className="relative">
                <input
                  type="number"
                  className={inputStyle + " pr-12"}
                  value={editValueText}
                  onChange={(e) => setEditValueText(e.target.value)}
                  min={1}
                  step={2}
                  autoFocus
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#888]">페이지</span>
              </div>
              <p className="text-[11px] text-[#999] mt-1">홀수 페이지만 입력 가능합니다</p>
            </div>
            {renderValuePriceEditor()}
            <EditDialogFooter
              onDelete={canDeleteEditingValue ? handleDeleteFromEditDialog : undefined}
              onCancel={closeEditDialog}
              onSave={handleUpdateValue}
              saveDisabled={!editValueText || Number(editValueText) % 2 === 0 || saving || hasDuplicateQuantityStep()}
            />
          </AddValueDialog>
        )}
      </>
    );
  };

  // 기본 렌더러 - 일반 값 (자재 연결 옵션 포함)
  const renderDefaultValues = () => {
    const otherMaterials = materials.filter((m) => m.type === "OTHER");
    const enabledValues = orderedAttributeValues.filter((v) => v.isEnabled);
    const disabledValues = orderedAttributeValues.filter((v) => !v.isEnabled);

    return (
      <>
        <div className={VALUE_GRID}>
          {/* 활성화된 값 표시 */}
          {sortableValues(enabledValues, "enabled", (val) => (
            <ValueCard
              key={val.id}
              label={val.value || "값 없음"}
              priceLabel={getValuePriceLabel(val)}
              imageUrl={attribute.previewType === "IMAGE_SELECT_BOX" ? val.imageUrl : undefined}
              onEdit={() => openEditDialog(val)}
              onRemove={val.isDeletable ? () => handleDeleteValue(val.id) : undefined}
            />
          ))}

          {/* 비활성화된 값도 표시 (흐리게) */}
          {sortableValues(disabledValues, "disabled", (val) => (
            <ValueCard
              key={val.id}
              label={val.value || "값 없음"}
              isDisabled
              imageUrl={attribute.previewType === "IMAGE_SELECT_BOX" ? val.imageUrl : undefined}
              onEdit={() => openEditDialog(val)}
              onRemove={val.isDeletable ? () => handleDeleteValue(val.id) : undefined}
            />
          ))}

          {attribute.isAddable && <AddValueCard onClick={() => setShowAddValue(true)} />}
        </div>

        {showAddValue && (
          <AddValueDialog title="선택 값 추가" onClose={closeDialog}>
            {/* 새 값 추가 섹션 */}
            {attribute.isAddable && (
              <>
                <div className="mb-4">
                  <label className="block text-[13px] font-medium text-[#555] mb-2">값 이름 *</label>
                  <input
                    type="text"
                    className={inputStyle}
                    value={newValueText}
                    onChange={(e) => setNewValueText(e.target.value)}
                    placeholder="예: 무광 코팅, 유광 코팅, 빨강, 파랑"
                    autoFocus
                  />
                  <p className="text-[11px] text-[#999] mt-1">고객이 선택할 때 보이는 이름입니다</p>
                </div>
                {attribute.previewType === "IMAGE_SELECT_BOX" && (
                  <div className="mb-4">
                    <label className="block text-[13px] font-medium text-[#555] mb-2">옵션 이미지 *</label>
                    <label className="flex min-h-[112px] cursor-pointer items-center justify-center rounded-[8px] border-2 border-dashed border-[#d0d0d0] bg-[#fafafa] p-3 hover:border-[#4285F4] transition-colors">
                      {newValueImagePreviewUrl ? (
                        <div className="flex items-center gap-3">
                          <div className="w-[84px] h-[84px] overflow-hidden rounded-[6px] border border-[#e0e0e0] bg-white">
                            <img src={newValueImagePreviewUrl} alt="옵션 이미지 미리보기" className="w-full h-full object-cover" />
                          </div>
                          <span className="max-w-[210px] truncate text-[12px] text-[#555]">{newValueImageFile?.name}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1 text-[#888]">
                          <Upload className="w-5 h-5" />
                          <span className="text-[12px]">클릭하여 이미지 첨부</span>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setNewValueImageFile(file);
                          setNewValueImagePreviewUrl(URL.createObjectURL(file));
                        }}
                      />
                    </label>
                    <p className="text-[11px] text-[#999] mt-1">고객이 이 옵션을 선택할 때 표시되는 이미지입니다</p>
                  </div>
                )}

                {/* 자재 연결 옵션 */}
                {otherMaterials.length > 0 && (
                  <div className="mb-4 border-t border-[#e8e8e8] pt-4">
                    <button
                      type="button"
                      onClick={() => setShowMaterialSelect(!showMaterialSelect)}
                      className="flex items-center gap-2 text-[13px] text-[#666] hover:text-[#4285F4]"
                    >
                      <Link2 className="w-[14px] h-[14px]" />
                      <span className="font-medium">자재 연결</span>
                      <span className="text-[#999]">{showMaterialSelect ? "(접기)" : "(고급)"}</span>
                    </button>
                    <p className="text-[11px] text-[#999] mt-1">재고 관리를 위해 등록된 자재와 연결할 수 있습니다</p>

                    {showMaterialSelect && (
                      <div className="mt-3 p-3 border border-[#e0e0e0] rounded-[8px] max-h-[150px] overflow-y-auto bg-white">
                        {otherMaterials.map((mat) => (
                          <label
                            key={mat.id}
                            className="flex items-center gap-2 py-1.5 hover:bg-[#f5f5f5] rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedMaterialIds.includes(mat.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMaterialIds((prev) => [...prev, mat.id]);
                                } else {
                                  setSelectedMaterialIds((prev) => prev.filter((id) => id !== mat.id));
                                }
                              }}
                              className="w-4 h-4 rounded border-[#d0d0d0] text-[#4285F4] focus:ring-[#4285F4]"
                            />
                            <span className="text-[13px] text-[#333]">{mat.name}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {selectedMaterialIds.length > 0 && (
                      <p className="text-[11px] text-[#4285F4] mt-2 font-medium">
                        ✓ {selectedMaterialIds.length}개 자재 선택됨
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <button onClick={closeDialog} className="px-4 py-2 text-[13px] text-[#666] hover:text-[#333]">취소</button>
                  <button
                    onClick={handleAddValue}
                    disabled={!newValueText.trim() || (attribute.previewType === "IMAGE_SELECT_BOX" && !newValueImageFile) || saving}
                    className="px-4 py-2 bg-[#222] text-white text-[13px] rounded-[8px] disabled:bg-[#e0e0e0] disabled:text-[#aaa]"
                  >
                    추가
                  </button>
                </div>
              </>
            )}
          </AddValueDialog>
        )}

        {/* 수정 다이얼로그 */}
        {showEditValue && editingValue && (
          <AddValueDialog title="값 수정" onClose={closeEditDialog}>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">값 이름 *</label>
              <input
                type="text"
                className={inputStyle}
                value={editValueText}
                onChange={(e) => setEditValueText(e.target.value)}
                placeholder="예: 무광 코팅, 유광 코팅"
                autoFocus
              />
            </div>
            {/* 이미지 선택형은 이미지가 필수라 삭제 없이 교체만 허용한다 */}
            {attribute.previewType === "IMAGE_SELECT_BOX" && (
              <ValueImageField
                previewUrl={editValueImagePreviewUrl}
                fileName={editValueImageFile?.name}
                onSelect={(file) => {
                  setEditValueImageFile(file);
                  setEditValueImagePreviewUrl(URL.createObjectURL(file));
                }}
                helpText="클릭하여 이미지를 변경할 수 있습니다"
              />
            )}
            {/* 활성화/비활성화 토글 */}
            <div className="mb-4">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-[13px] font-medium text-[#555]">활성화 상태</span>
                <button
                  type="button"
                  onClick={() => setEditValueEnabled(!editValueEnabled)}
                  className={`relative w-[44px] h-[24px] rounded-full transition-colors ${
                    editValueEnabled ? "bg-[#4285F4]" : "bg-[#ddd]"
                  }`}
                >
                  <span
                    className={`absolute top-[2px] w-[20px] h-[20px] bg-white rounded-full shadow transition-transform ${
                      editValueEnabled ? "left-[22px]" : "left-[2px]"
                    }`}
                  />
                </button>
              </label>
              <p className="text-[11px] text-[#888] mt-1">
                {editValueEnabled ? "고객이 이 값을 선택할 수 있습니다" : "고객에게 표시되지 않습니다"}
              </p>
            </div>
            {renderValuePriceEditor()}
            <EditDialogFooter
              onDelete={canDeleteEditingValue ? handleDeleteFromEditDialog : undefined}
              onCancel={closeEditDialog}
              onSave={handleUpdateValue}
              saveDisabled={!editValueText.trim() || saving || hasDuplicateQuantityStep()}
            />
          </AddValueDialog>
        )}
      </>
    );
  };

  /**모든 옵션 속성에 공통으로 붙는 기본값 선택 줄.
   *
   * 값이 하나뿐이면 바꿀 여지가 없으므로 표시하지 않는다.
   * 비활성 값도 목록에는 남겨 둔다 — 이미 기본값인 값이 비활성화된 경우 그 상태가 보여야 하기 때문이다.
   */
  const renderDefaultValueSelector = () => {
    // 순서를 바꾸면 바로 새 순서로 보이도록 화면의 현재 순서를 쓴다
    const orderedValues = orderedAttributeValues;
    if (orderedValues.length < 2) return null;

    return (
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#eee]">
        <label className="text-[12px] font-medium text-[#555] shrink-0">기본값</label>
        <select
          value={attribute.defaultAttributeValueId ?? ""}
          disabled={saving}
          onChange={(e) => handleChangeDefaultValue(Number(e.target.value))}
          className="border border-[#e0e0e0] rounded-[8px] px-3 py-1.5 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none transition-colors disabled:bg-[#f5f5f5] disabled:text-[#aaa]"
        >
          {attribute.defaultAttributeValueId == null && (
            <option value="">선택 안 함</option>
          )}
          {orderedValues.map((val) => (
            <option key={val.id} value={val.id}>
              {(val.value?.trim() || getValueTypeLabel(val.type) || `값 #${val.id}`) +
                (val.isEnabled ? "" : " (비활성)")}
            </option>
          ))}
        </select>
        <span className="text-[11px] text-[#888]">쇼핑몰에서 처음부터 선택돼 있는 값입니다.</span>
      </div>
    );
  };

  // Route to the right renderer
  const renderValues = () => {
    switch (sysType) {
      case "BINDING_TYPE":
        return renderBindingTypeValues();
      case "BINDING_DIRECTION":
        return renderBindingDirectionValues();
      case "PAPER_ORIENTATION":
        return renderPaperOrientationValues();
      case "END_PAPER_PLACEMENT":
        return renderEndPaperPlacementValues();
      case "PAPER_TYPE":
        return renderPaperTypeValues();
      case "GRAMMAGE":
        return renderGrammageValues();
      case "PRINT_COLOR_MODE":
        return renderPrintColorModeValues();
      case "INSERT_PAGE_NUMBER":
        return renderInsertPageNumberValues();
      default:
        return renderDefaultValues();
    }
  };

  return (
    <>
      <ReorderError message={valueReorder.error} onClose={valueReorder.clearError} />
      {renderValues()}
      {renderDefaultValueSelector()}
    </>
  );
};
